import {
    AppstoreOutlined,
    BankOutlined,
    BookOutlined,
    ContainerOutlined,
    EuroOutlined,
    HomeOutlined,
    ProfileOutlined,
    ProjectOutlined,
    RiseOutlined,
    ScheduleOutlined,
    SettingOutlined,
    SolutionOutlined,
    SwapOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { useUser } from "@authentication/presentation/hooks/use-user.hook";
import { IncCol } from "@core/presentacion/component/layout/col/inc-col.component";
import { IncRow } from "@core/presentacion/component/layout/row/inc-row.component";
import { AdminPaths, AdminRoutes } from "@routes/private/admin.routes";
import { BeneficiaryRoutes } from "@routes/private/beneficiary.routes";
import { CatalogueRoutes } from "@routes/private/catalogue.routes";
import { EntityPaths, EntityRoutes } from "@routes/private/entity.routes";
import { HomeRoutes } from "@routes/private/home.routes";
import { ProjectPaths, ProjectRoutes } from "@routes/private/project.routes";
import { UserRoutes } from "@routes/private/user.routes";
import { Tooltip } from "antd";
import { MenuItemGroupType, MenuItemType } from "antd/es/menu/hooks/useItems";
import { MenuClickEventHandler } from "rc-menu/lib/interface";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../../../../../../public/img/logo.png";
import { IncMenu } from "../../navigation/menu/inc-menu.component";
import { IncSider } from "../sider/inc-sider.component";
import * as styles from "./sider-menu.module.scss";

const PUBLIC_API_ENABLED = process.env.PUBLIC_API_ENABLED === "true";

export const IncSiderMenu: FC = () => {
    const navigate = useNavigate();
    const route = useLocation();
    const { t } = useTranslation();
    const user = useUser();

    const SIDER_WIDTH = 300;
    const MENU_FONT_WEIGHT = 400;

    // Estado para controlar manualmente qué submenús están abiertos
    const [manuallyOpenKeys, setManuallyOpenKeys] = useState<string[]>([]);

    const handleClick: MenuClickEventHandler = useCallback(
        (info): void => {
            // Redirección especial para Ingresos
            if (info.key === "/entity/revenues/list") {
                if (user.entityId) {
                    navigate(EntityRoutes.LIST_REVENUES(user.entityId));
                } else {
                    // Si no hay entidad seleccionada, puedes mostrar un error o redirigir a una página por defecto
                }
                return;
            }
            navigate(info.key);
        },
        [navigate, user.entityId],
    );

    // We have to use this method when title is too long
    const getTitleTooltip = (title: string): JSX.Element => (
        <Tooltip
            title={title}
            placement={"right"}
            className={"tooltip"}
        >
            <label>{title}</label>
        </Tooltip>
    );

    const menuItems: (MenuItemType | MenuItemGroupType)[] = useMemo(
        () => [
            {
                label: t("component:layout.incSiderMenu.home"),
                key: HomeRoutes.HOME,
                icon: <HomeOutlined />,
                style: { fontWeight: MENU_FONT_WEIGHT },
            },
            {
                label: t("component:layout.incSiderMenu.userMenuLabel"),
                key: UserRoutes.LIST,
                icon: <UserOutlined />,
                style: { fontWeight: MENU_FONT_WEIGHT },
            },
            {
                label: t("component:layout.incSiderMenu.entity.subMenuLabel"),
                key: EntityPaths.Root,
                icon: <HomeOutlined />,
                style: { fontWeight: MENU_FONT_WEIGHT },
                children: [
                    {
                        label: t("component:layout.incSiderMenu.generalData"),
                        key: EntityRoutes.LIST,
                        icon: <HomeOutlined />,
                        style: { fontWeight: MENU_FONT_WEIGHT },
                    },
                    ...(PUBLIC_API_ENABLED
                        ? [
                              {
                                  label: t(
                                      "component:layout.incSiderMenu.entity.apiKeysLabel",
                                  ),
                                  key: EntityRoutes.ENTITY_API_KEYS_LIST,
                                  icon: <BookOutlined />,
                              },
                          ]
                        : []),
                    {
                        label: t(
                            "component:layout.incSiderMenu.entity.employeeLabel",
                        ),
                        key: EntityRoutes.LIST_EMPLOYEE,
                        icon: <UserOutlined />,
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.entity.materialLabel",
                        ),
                        key: EntityRoutes.LIST_MATERIAL,
                        icon: <ContainerOutlined />,
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.entity.economicMangemet",
                        ),
                        key: "gestion_economica",
                        icon: <EuroOutlined />,
                        children: [
                            {
                                label: t(
                                    "component:layout.incSiderMenu.entity.revenueLabel",
                                ),
                                key: "/entity/revenues/list", // clave igual a la ruta real
                                icon: <EuroOutlined />,
                            },
                            {
                                label: t(
                                    "component:layout.incSiderMenu.entity.invoicesLabel",
                                ),
                                key: EntityRoutes.LIST_COST,
                                icon: <EuroOutlined />,
                            },
                        ],
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.entity.commercialRelationLabel",
                        ),
                        key: "relaciones_comerciales",
                        icon: <BankOutlined />,
                        children: [
                            {
                                label: t(
                                    "component:layout.incSiderMenu.entity.creditorsLabel",
                                ),
                                key: EntityRoutes.LIST_CREDITORS,
                                icon: <BankOutlined />,
                            },
                            {
                                label: t(
                                    "component:layout.incSiderMenu.entity.supplierLabel",
                                ),
                                key: EntityRoutes.LIST_SUPPLIER,
                                icon: <AppstoreOutlined />,
                            },
                        ],
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.project.projectMenuLabel",
                        ),
                        key: ProjectRoutes.LIST,
                        icon: <ProjectOutlined />,
                        style: { fontWeight: MENU_FONT_WEIGHT },
                    },
                ],
            },
            {
                label: t("component:layout.incSiderMenu.beneficiaryMenuLabel"),
                key: "beneficiary_menu",
                icon: <SolutionOutlined />,
                children: [
                    {
                        label: t(
                            "component:layout.incSiderMenu.beneficiary.registerLabel",
                        ),
                        key: BeneficiaryRoutes.LIST,
                        icon: <ScheduleOutlined />,
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.catalogueMenuLabel",
                        ),
                        key: CatalogueRoutes.LIST,
                        icon: <ProfileOutlined />,
                        style: { fontWeight: MENU_FONT_WEIGHT },
                        children: [
                            {
                                label: t(
                                    "component:layout.incSiderMenu.catalogue.servicesLabel",
                                ),
                                key: CatalogueRoutes.SERVICES,
                                icon: <RiseOutlined />,
                            },
                            {
                                label: t(
                                    "component:layout.incSiderMenu.catalogue.derivationsLabel",
                                ),
                                key: CatalogueRoutes.DERIVATIONS,
                                icon: <SwapOutlined />,
                            },
                        ],
                    },
                ],
                style: { fontWeight: MENU_FONT_WEIGHT },
            },
            {
                label: t("component:layout.incSiderMenu.admin.adminMenuLabel"),
                key: AdminPaths.Root,
                icon: <SettingOutlined />,
                style: { fontWeight: MENU_FONT_WEIGHT },
                children: [
                    {
                        label: t(
                            "component:layout.incSiderMenu.admin.financialSourcesLabel",
                        ),
                        key: AdminRoutes.FINANCIAL_SOURCES,
                        icon: <BankOutlined />,
                        style: { fontWeight: MENU_FONT_WEIGHT },
                    },
                    {
                        label: t(
                            "component:layout.incSiderMenu.admin.userManagementLabel",
                        ),
                        key: AdminRoutes.USER_MANAGEMENT,
                        icon: <UserOutlined />,
                        style: { fontWeight: MENU_FONT_WEIGHT },
                    },
                    ...(PUBLIC_API_ENABLED
                        ? [
                              {
                                  label: t(
                                      "component:layout.incSiderMenu.admin.entityApiKeysLabel",
                                  ),
                                  key: AdminRoutes.ENTITY_API_KEYS_LIST,
                                  icon: <BookOutlined />,
                                  style: { fontWeight: MENU_FONT_WEIGHT },
                              },
                              {
                                  label: t(
                                      "component:layout.incSiderMenu.admin.publicApiUsageStatsLabel",
                                  ),
                                  key: AdminRoutes.PUBLIC_API_USAGE_STATS,
                                  icon: <ScheduleOutlined />,
                                  style: { fontWeight: MENU_FONT_WEIGHT },
                              },
                          ]
                        : []),
                ],
            },
        ],
        [t],
    );

    const authorizedMenuItems: (MenuItemType | MenuItemGroupType)[] =
        useMemo(() => {
            let filteredMenuItems = menuItems;

            if (user.isProfessional()) {
                filteredMenuItems = menuItems.filter(
                    (menuItem) => menuItem.key !== ProjectPaths.Root,
                );
            }

            // Filter admin menu based on user roles
            filteredMenuItems = filteredMenuItems
                .map((menuItem) => {
                    if (menuItem.key === EntityPaths.Root) {
                        const entityMenuItem = menuItem as MenuItemGroupType;
                        const filteredChildren =
                            entityMenuItem.children?.filter((child) => {
                                const childKey = child!.key!.toString();

                                if (
                                    childKey ===
                                    EntityRoutes.ENTITY_API_KEYS_LIST
                                ) {
                                    return user.isEntityAdmin();
                                }

                                return true;
                            });

                        return {
                            ...entityMenuItem,
                            children: filteredChildren,
                        };
                    }

                    if (menuItem.key === AdminPaths.Root) {
                        // Only show admin menu to SuperAdmin and EntityAdmin users
                        if (!user.isSuperAdmin() && !user.isAdminRole()) {
                            return null;
                        }

                        // Filter admin submenu items based on role
                        const adminMenuItem = menuItem as MenuItemGroupType;
                        const filteredChildren = adminMenuItem.children?.filter(
                            (child) => {
                                const childKey = child!.key!.toString();

                                // Entity Management and System Settings are SuperAdmin only
                                if (
                                    childKey ===
                                        AdminRoutes.ENTITY_MANAGEMENT ||
                                    childKey === AdminRoutes.SYSTEM_SETTINGS
                                ) {
                                    return user.isSuperAdmin();
                                }

                                // Other admin features are available to both SuperAdmin and EntityAdmin
                                return true;
                            },
                        );

                        return {
                            ...adminMenuItem,
                            children: filteredChildren,
                        };
                    }
                    return menuItem;
                })
                .filter(Boolean) as (MenuItemType | MenuItemGroupType)[];

            return filteredMenuItems;
        }, [menuItems, user]);

    const selecteMenuItem = useMemo(() => {
        // Function to recursively search for matching menu item
        const findMatchingMenuItem = (
            items: (MenuItemType | MenuItemGroupType)[],
        ): string => {
            for (const menuItem of items) {
                // Check if this menu item has children (submenu)
                if (Object.hasOwn(menuItem, "children")) {
                    const subMenu = (
                        menuItem as MenuItemGroupType
                    ).children!.filter(Boolean) as (
                        | MenuItemType
                        | MenuItemGroupType
                    )[];

                    // Recursively search in children first (more specific routes)
                    const childMatch = findMatchingMenuItem(subMenu);
                    if (childMatch) {
                        return childMatch;
                    }
                }

                // Check if current route matches this menu item
                const menuKey = menuItem.key!.toString();
                if (
                    route.pathname === menuKey ||
                    route.pathname.startsWith(menuKey + "/")
                ) {
                    return menuKey;
                }
            }
            return "";
        };

        return findMatchingMenuItem(authorizedMenuItems);
    }, [authorizedMenuItems, route.pathname]);

    const autoOpenKeys = useMemo(() => {
        let keys: string[] = [];

        // Function to recursively find parent keys that should be open
        const findOpenKeys = (
            items: (MenuItemType | MenuItemGroupType)[],
            parentKeys: string[] = [],
        ): string[] => {
            for (const menuItem of items) {
                const currentKeys = [...parentKeys, menuItem.key!.toString()];

                if (Object.hasOwn(menuItem, "children")) {
                    const subMenu = (
                        menuItem as MenuItemGroupType
                    ).children!.filter(Boolean) as (
                        | MenuItemType
                        | MenuItemGroupType
                    )[];

                    // Check if any child matches the current route
                    const childKeys = findOpenKeys(subMenu, currentKeys);
                    if (childKeys.length > 0) {
                        return childKeys;
                    }

                    // Check if current menu item itself contains the route
                    if (route.pathname.startsWith(menuItem.key!.toString())) {
                        return currentKeys.slice(0, -1); // Return parent keys without the current item
                    }
                }

                // For leaf items, check if they match
                if (
                    route.pathname === menuItem.key!.toString() ||
                    route.pathname.startsWith(menuItem.key!.toString() + "/")
                ) {
                    return parentKeys; // Return parent keys
                }
            }
            return [];
        };

        keys = findOpenKeys(authorizedMenuItems);
        return keys;
    }, [authorizedMenuItems, route.pathname]);

    // Combinar claves automáticas con claves manuales, eliminando duplicados
    const finalOpenKeys = useMemo(() => {
        const combined = [...new Set([...autoOpenKeys, ...manuallyOpenKeys])];
        return combined;
    }, [autoOpenKeys, manuallyOpenKeys]);

    // Handler para cuando el usuario abre/cierra submenús manualmente
    const handleOpenChange = useCallback((openKeys: string[]) => {
        setManuallyOpenKeys(openKeys);
    }, []);

    // Sincronizar manuallyOpenKeys con autoOpenKeys cuando cambie la ruta
    useEffect(() => {
        setManuallyOpenKeys((prevKeys) => {
            // Combinar las claves automáticas con las existentes manuales
            const combined = [...new Set([...autoOpenKeys, ...prevKeys])];
            return combined;
        });
    }, [autoOpenKeys]);

    return (
        <IncSider
            collapsible
            width={SIDER_WIDTH}
        >
            <IncRow>
                <IncCol span={24}>
                    <img
                        alt={"logo"}
                        width={400}
                        height={300}
                        className={`${styles.logo} tw-px-3`}
                        src={logo}
                    />
                </IncCol>
            </IncRow>
            <div className={styles["menu-scroll-container"]}>
                <IncMenu
                    selectedKeys={[selecteMenuItem]}
                    openKeys={finalOpenKeys}
                    onOpenChange={handleOpenChange}
                    theme={"dark"}
                    mode={"inline"}
                    items={authorizedMenuItems}
                    onClick={handleClick}
                />
            </div>
        </IncSider>
    );
};
